import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { formatCurrency } from "../utils/currency";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Divider,
  Input,
  Select,
  Text,
  Title2,
  Title3,
} from "@fluentui/react-components";
import {
  ArrowClockwise24Regular,
  Save24Regular,
  Warning24Regular,
} from "@fluentui/react-icons";

import DynamicsTable from "./ui/DynamicsTable";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5050";

function createId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `row_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

async function calculateQuoteFromBackend(parsed, requestText = "") {
  const res = await fetch(`${API_BASE}/api/calculate-quote`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      parsedData: parsed,
      requestText:
        requestText || parsed.requestText || parsed.request_text || "",
    }),
  });

  if (!res.ok) {
    const errorText = await res.text().catch(() => "");
    throw new Error(errorText || "Calculation failed");
  }

  return res.json();
}

function safeObject(value, fallback = {}) {
  if (!value) return fallback;

  if (typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed)
        ? parsed
        : fallback;
    } catch {
      return fallback;
    }
  }

  return fallback;
}

function normalizeRows(rows = [], type) {
  if (!Array.isArray(rows)) return [];

  return rows.map((row, index) => {
    const safeRow = typeof row === "object" && row !== null ? row : {};

    const quantity = Number(
      safeRow.quantity ?? safeRow.qty ?? safeRow.hours ?? 0
    );

    const unitPrice = Number(
      safeRow.unitPrice ??
        safeRow.price ??
        safeRow.hourlyRate ??
        safeRow.rate ??
        0
    );

    const total =
      safeRow.total !== undefined
        ? Number(safeRow.total || 0)
        : quantity * unitPrice;

    const meta =
      typeof safeRow.meta === "object" && safeRow.meta !== null
        ? safeRow.meta
        : {};

    return {
      ...safeRow,
      id: safeRow.id || `${type}_${index + 1}`,
      type: safeRow.type || type,
      label: safeRow.label || safeRow.name || "Untitled row",
      name: safeRow.name || safeRow.label || "Untitled row",
      quantity,
      unitPrice,
      total,
      unit: safeRow.unit || (type === "labour" ? "hour" : ""),
      reviewRequired: Boolean(
        safeRow.reviewRequired ||
          safeRow.flags?.reviewRequired ||
          meta.review
      ),
      materialLibraryMatch:
        safeRow.materialLibraryMatch !== undefined
          ? safeRow.materialLibraryMatch
          : meta.materialLibraryMatch !== undefined
          ? meta.materialLibraryMatch
          : undefined,
      labourRuleMatch:
        safeRow.labourRuleMatch !== undefined
          ? safeRow.labourRuleMatch
          : meta.labourRuleActivity !== undefined
          ? meta.labourRuleActivity
          : undefined,
      meta,
    };
  });
}

function normalizeEstimate(value) {
  const estimate = safeObject(value, {});

  return {
    ...estimate,
    materials: normalizeRows(estimate.materials, "material"),
    labour: normalizeRows(estimate.labour, "labour"),
    extras: normalizeRows(estimate.extras, "extra"),
    summary: safeObject(estimate.summary, {
      materialsSubtotal: 0,
      labourSubtotal: 0,
      extrasSubtotal: 0,
      baseCost: 0,
      marginPercent: 25,
      marginAmount: 0,
      subtotal: 0,
      vat: 0,
      vatAmount: 0,
      vatPercent: 25,
      total: 0,
    }),
    meta: safeObject(estimate.meta, {}),
  };
}

function round2(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function recalculateEstimateTotals(estimate) {
  const materials = (estimate.materials || []).map((row) => {
    const quantity = Number(row.quantity ?? row.qty ?? 0);
    const unitPrice = Number(row.unitPrice ?? row.price ?? 0);

    return {
      ...row,
      type: "material",
      quantity,
      qty: quantity,
      unitPrice,
      price: unitPrice,
      total: round2(quantity * unitPrice),
    };
  });

  const labour = (estimate.labour || []).map((row) => {
    const quantity = Number(row.quantity ?? row.hours ?? 0);
    const unitPrice = Number(row.unitPrice ?? row.hourlyRate ?? row.rate ?? 0);

    return {
      ...row,
      type: "labour",
      quantity,
      hours: quantity,
      unit: row.unit || "hour",
      unitPrice,
      hourlyRate: unitPrice,
      rate: unitPrice,
      total: round2(quantity * unitPrice),
    };
  });

  const extras = (estimate.extras || []).map((row) => {
    const quantity = Number(row.quantity ?? row.qty ?? 0);
    const unitPrice = Number(row.unitPrice ?? row.price ?? 0);

    return {
      ...row,
      type: "extra",
      quantity,
      qty: quantity,
      unitPrice,
      price: unitPrice,
      total: round2(quantity * unitPrice),
    };
  });

  const materialsSubtotal = round2(
    materials.reduce((sum, row) => sum + Number(row.total || 0), 0)
  );

  const labourSubtotal = round2(
    labour.reduce((sum, row) => sum + Number(row.total || 0), 0)
  );

  const extrasSubtotal = round2(
    extras.reduce((sum, row) => sum + Number(row.total || 0), 0)
  );

  const marginPercent = Number(
    estimate?.summary?.marginPercent ?? estimate?.meta?.marginPercent ?? 25
  );

  const vatPercent = Number(
    estimate?.summary?.vatPercent ?? estimate?.meta?.vatPercent ?? 25
  );

  const baseCost = round2(materialsSubtotal + labourSubtotal + extrasSubtotal);
  const marginAmount = round2(baseCost * (marginPercent / 100));
  const subtotal = round2(baseCost + marginAmount);
  const vat = round2(subtotal * (vatPercent / 100));
  const vatAmount = vat;
  const total = round2(subtotal + vat);

  return {
    ...estimate,
    materials,
    labour,
    extras,
    summary: {
      ...(estimate.summary || {}),
      materialsSubtotal,
      labourSubtotal,
      extrasSubtotal,
      baseCost,
      marginPercent,
      marginAmount,
      subtotal,
      vat,
      vatAmount,
      vatPercent,
      total,
    },
    meta: {
      ...(estimate.meta || {}),
      marginPercent,
      vatPercent,
    },
  };
}

function emptyRow(type) {
  if (type === "labour") {
    return {
      id: createId(),
      type: "labour",
      label: "",
      quantity: 0,
      unit: "hour",
      unitPrice: 0,
      hours: 0,
      hourlyRate: 0,
      total: 0,
      meta: {},
    };
  }

  if (type === "extra") {
    return {
      id: createId(),
      type: "extra",
      label: "",
      quantity: 0,
      unit: "",
      unitPrice: 0,
      qty: 0,
      price: 0,
      total: 0,
      meta: {},
    };
  }

  return {
    id: createId(),
    type: "material",
    label: "",
    quantity: 0,
    unit: "",
    unitPrice: 0,
    qty: 0,
    price: 0,
    total: 0,
    meta: {},
  };
}

export default function QuoteEditor({ quoteId: propQuoteId }) {
  const { id } = useParams();
  const quoteId = propQuoteId || id;

  const [parsed, setParsed] = useState({});
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  useEffect(() => {
    loadQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  async function loadQuote() {
    if (!quoteId) {
      setErrorMessage("Missing quote ID");
      setEstimate(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");
      setSaveMessage("");

      const { data, error } = await supabase
        .from("quotes")
        .select("*")
        .eq("id", quoteId)
        .single();

      if (error) {
        throw new Error(error.message || "Failed to load quote");
      }

      if (!data) {
        throw new Error("Quote not found");
      }

      const parsedBase = safeObject(data.parsed_json, {});
      const estimateData = recalculateEstimateTotals(
        normalizeEstimate(data.estimate_json)
      );

      const parsedData = {
        ...parsedBase,
        requestText:
          parsedBase.requestText ||
          parsedBase.request_text ||
          data.request_text ||
          "",
        request_text:
          parsedBase.request_text ||
          parsedBase.requestText ||
          data.request_text ||
          "",
        marginPercent:
          parsedBase.marginPercent ??
          estimateData.summary?.marginPercent ??
          25,
      };

      setParsed(parsedData);
      setEstimate(estimateData);
    } catch (err) {
      console.error("loadQuote failed:", err);
      setErrorMessage(err.message || "Failed to load quote");
      setEstimate(null);
    } finally {
      setLoading(false);
    }
  }

  async function rebuild() {
    try {
      setLoading(true);
      setErrorMessage("");
      setSaveMessage("");

      const result = await calculateQuoteFromBackend(
        parsed,
        parsed.requestText || parsed.request_text || ""
      );

      const normalized = recalculateEstimateTotals(normalizeEstimate(result));
      setEstimate(normalized);
    } catch (err) {
      console.error(err);
      setErrorMessage(err.message || "Rebuild failed");
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    if (!quoteId) {
      setErrorMessage("Missing quote ID");
      return;
    }

    try {
      setSaving(true);
      setErrorMessage("");
      setSaveMessage("");

      const normalizedEstimate = recalculateEstimateTotals(
        normalizeEstimate(estimate)
      );

      const { error } = await supabase
        .from("quotes")
        .update({
          parsed_json: parsed,
          estimate_json: normalizedEstimate,
        })
        .eq("id", quoteId);

      if (error) {
        throw new Error(error.message || "Failed to save quote");
      }

      setEstimate(normalizedEstimate);
      setSaveMessage("Saved");
    } catch (err) {
      console.error("save failed:", err);
      setErrorMessage(err.message || "Failed to save quote");
    } finally {
      setSaving(false);
    }
  }

  function updateParsed(field, value) {
    setParsed((prev) => ({ ...prev, [field]: value }));

    if (field === "marginPercent") {
      setEstimate((prev) => {
        const safeEstimate = normalizeEstimate(prev);

        return recalculateEstimateTotals({
          ...safeEstimate,
          summary: {
            ...safeEstimate.summary,
            marginPercent: Number(value || 0),
          },
          meta: {
            ...safeEstimate.meta,
            marginPercent: Number(value || 0),
          },
        });
      });
    }
  }

  function updateRow(type, index, field, value) {
    setEstimate((prev) => {
      const safeEstimate = normalizeEstimate(prev);
      const updated = [...safeEstimate[type]];

      updated[index] = {
        ...updated[index],
        [field]: value,
      };

      return recalculateEstimateTotals({
        ...safeEstimate,
        [type]: updated,
      });
    });
  }

  function addRow(type) {
    const rowType =
      type === "materials"
        ? "material"
        : type === "labour"
        ? "labour"
        : type === "extras"
        ? "extra"
        : type;

    setEstimate((prev) => {
      const safeEstimate = normalizeEstimate(prev);

      return recalculateEstimateTotals({
        ...safeEstimate,
        [type]: [...safeEstimate[type], emptyRow(rowType)],
      });
    });
  }

  function removeRow(type, index) {
    setEstimate((prev) => {
      const safeEstimate = normalizeEstimate(prev);
      const updated = [...safeEstimate[type]];
      updated.splice(index, 1);

      return recalculateEstimateTotals({
        ...safeEstimate,
        [type]: updated,
      });
    });
  }

  const currency = parsed?.currency || "DKK";

  if (loading) {
    return (
      <div className="min-h-full bg-[#f3f2f1] p-6 text-[#201f1e]">
        <Card className="max-w-xl border-l-4 border-l-[#0f6cbd]">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Title3>Loading quote</Title3>
              <Text size={300} className="text-[#605e5c]">
                Fetching quote data and recalculating totals.
              </Text>
            </div>
            <Badge appearance="tint" color="brand">
              Loading
            </Badge>
          </div>
        </Card>
      </div>
    );
  }

  if (errorMessage && !estimate) {
    return (
      <div className="min-h-full bg-[#f3f2f1] p-6 text-[#201f1e]">
        <Card className="max-w-xl border-l-4 border-l-[#d13438]">
          <div className="flex items-start gap-3 text-[#a4262c]">
            <Warning24Regular className="shrink-0" />
            <div>
              <Title3>Could not load quote</Title3>
              <Text>{errorMessage}</Text>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="min-h-full bg-[#f3f2f1] p-6 text-[#201f1e]">
        <Card className="max-w-xl">
          <Title3>Quote not found</Title3>
          <Text size={300} className="text-[#605e5c]">
            No estimate data was found for this quote.
          </Text>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#f3f2f1] p-6 text-[#201f1e]">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <Title2>Quote Editor</Title2>
          <div className="mt-1">
            <Text size={300} className="text-[#605e5c]">
              Review parsed scope, estimate rows, margin, VAT, and final price.
            </Text>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            appearance="secondary"
            icon={<ArrowClockwise24Regular />}
            onClick={rebuild}
            disabled={loading}
          >
            Rebuild
          </Button>

          <Button
            appearance="primary"
            icon={<Save24Regular />}
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {errorMessage && (
        <Card className="mb-4 border-l-4 border-l-[#d13438]">
          <div className="flex items-center gap-2 text-[#a4262c]">
            <Warning24Regular />
            <Text>{errorMessage}</Text>
          </div>
        </Card>
      )}

      {saveMessage && (
        <Card className="mb-4 border-l-4 border-l-[#107c10]">
          <Text className="text-[#107c10]">{saveMessage}</Text>
        </Card>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader
              header={<Title3>Parsed Input</Title3>}
              description="Main quote assumptions from the parsed request"
            />

            <Divider />

            <div className="grid gap-4 pt-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="space-y-1">
                <Text weight="semibold">Floor area</Text>
                <Input
                  type="number"
                  value={String(parsed.floorAreaM2 ?? "")}
                  contentAfter="m²"
                  onChange={(e) =>
                    updateParsed(
                      "floorAreaM2",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                />
              </div>

              <div className="space-y-1">
                <Text weight="semibold">Flooring type</Text>
                <Select
                  value={parsed.flooringType || ""}
                  onChange={(e) =>
                    updateParsed("flooringType", e.target.value)
                  }
                >
                  <option value="">Select flooring</option>
                  <option value="laminate">Laminate</option>
                  <option value="wood">Wood</option>
                  <option value="vinyl">Vinyl</option>
                </Select>
              </div>

              <div className="space-y-1">
                <Text weight="semibold">Margin</Text>
                <Input
                  type="number"
                  value={String(
                    estimate.summary?.marginPercent ??
                      parsed.marginPercent ??
                      25
                  )}
                  contentAfter="%"
                  onChange={(e) =>
                    updateParsed(
                      "marginPercent",
                      e.target.value === "" ? 0 : Number(e.target.value)
                    )
                  }
                />
              </div>

              <div className="space-y-1">
                <Text weight="semibold">Currency</Text>
                <Input value={currency} disabled />
              </div>
            </div>
          </Card>

          {[
            { key: "materials", title: "Materials" },
            { key: "labour", title: "Labour" },
            { key: "extras", title: "Extras" },
          ].map(({ key, title }) => (
            <DynamicsTable
              key={key}
              title={title}
              rows={estimate[key] || []}
              currency={currency}
              onAdd={() => addRow(key)}
              onRemove={(index) => removeRow(key, index)}
              onChange={(index, field, value) =>
                updateRow(key, index, field, value)
              }
            />
          ))}
        </div>

        <aside className="space-y-4">
          <Card className="sticky top-6">
            <CardHeader header={<Title3>Summary</Title3>} />
            <Divider />

            <div className="space-y-3 pt-3">
              {[
                ["Materials", estimate.summary?.materialsSubtotal],
                ["Labour", estimate.summary?.labourSubtotal],
                ["Extras", estimate.summary?.extrasSubtotal],
                ["Base cost", estimate.summary?.baseCost],
                [
                  `Margin ${estimate.summary?.marginPercent ?? 25}%`,
                  estimate.summary?.marginAmount,
                ],
                ["Subtotal excl. VAT", estimate.summary?.subtotal],
                [
                  `VAT ${estimate.summary?.vatPercent ?? 25}%`,
                  estimate.summary?.vatAmount,
                ],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4 text-sm">
                  <Text className="text-[#605e5c]">{label}</Text>
                  <Text weight="semibold">
                    {formatCurrency(value || 0, currency)}
                  </Text>
                </div>
              ))}

              <Divider />

              <div className="flex items-end justify-between gap-4">
                <div>
                  <Text weight="bold">Total incl. VAT</Text>
                  <div>
                    <Text size={200} className="text-[#605e5c]">
                      Customer price
                    </Text>
                  </div>
                </div>

                <Text weight="bold" size={500} className="text-[#107c10]">
                  {formatCurrency(estimate.summary?.total || 0, currency)}
                </Text>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader header={<Title3>Status</Title3>} />

            <div className="flex flex-wrap gap-2">
              <Badge appearance="filled" color="brand">
                {currency}
              </Badge>

              <Badge appearance="tint" color="success">
                Margin {estimate.summary?.marginPercent ?? 25}%
              </Badge>

              {(estimate.meta?.reviewFlags || []).length > 0 && (
                <Badge appearance="tint" color="danger">
                  Review needed
                </Badge>
              )}
            </div>
          </Card>
        </aside>
      </div>
    </div>
  );
}